import React from "react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { supabase } from "@/lib/supabase";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Stats from "@/pages/stats";
import CalendarPage from "@/pages/calendar";
import InvoicePage from "@/pages/invoice";
import TaxesPage from "@/pages/taxes";
import SettingsPage from "@/pages/settings";
import HelpPage from "@/pages/help";
import AdminPage from "@/pages/admin";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import OnboardingPage from "@/pages/onboarding";
import ImportPage from "@/pages/import";
import LoginPage from "@/pages/login";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { TrialExpiredGate } from "@/components/TrialExpiredGate";
import { TrialBanner } from "@/components/TrialBanner";
import { SubscriptionModal } from "@/components/SubscriptionModal";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/sidebar";
import { useState, createContext, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { cn } from "@/lib/utils";
import { type HolidayCalendar } from "@/lib/holidays";
import { 
  Home as HomeIcon, 
  PieChart as PieChartIcon, 
  CalendarDays as CalendarDaysIcon, 
  Receipt as ReceiptIcon, 
  Calculator as CalculatorIcon,
  HelpCircle as HelpCircleIcon,
  Settings as SettingsIcon
} from "lucide-react";

const ThemeContext = createContext<{
  isDark: boolean;
  toggleTheme: () => void;
  currency: string;
  toggleCurrency: () => void;
  setCurrencyDirect: (v: string) => void;
  dateFormat: "auto" | "us" | "eu";
  setDateFormat: (v: "auto" | "us" | "eu") => void;
  holidayCalendar: HolidayCalendar;
  setHolidayCalendar: (v: HolidayCalendar) => void;
}>({
  isDark: false,   // MUST stay false — light is the permanent default. Never set this to true.
  toggleTheme: () => {},
  currency: "USD",
  toggleCurrency: () => {},
  setCurrencyDirect: () => {},
  dateFormat: "auto",
  setDateFormat: () => {},
  holidayCalendar: "US",
  setHolidayCalendar: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

const PUBLIC_ROUTES = ['/login', '/onboarding', '/privacy', '/terms'];

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, onboardingComplete } = useAuth();
  const location = useLocation();
  if (loading) return (
    // Show a centred spinner instead of a blank page while auth + onboarding status resolves.
    <div className="flex-1 min-h-screen flex items-center justify-center bg-[#E3E8F1]">
      <div className="w-7 h-7 rounded-full border-[3px] border-[#4A6CF7] border-t-transparent animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;   // no session — go to login
  if (onboardingComplete === null) return <Navigate to="/login" state={{ from: location }} replace />; // timed out — re-auth
  if (!onboardingComplete) return <Navigate to="/onboarding" replace />; // genuine new user — finish onboarding
  // TrialExpiredGate handles isTrialExpired + past_due states internally
  return <TrialExpiredGate>{children}</TrialExpiredGate>;
}

function Router() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/import" element={<RequireAuth><ImportPage /></RequireAuth>} />
      <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
      <Route path="/stats" element={<RequireAuth><Stats /></RequireAuth>} />
      <Route path="/calendar" element={<RequireAuth><CalendarPage /></RequireAuth>} />
      <Route path="/invoice" element={<RequireAuth><InvoicePage /></RequireAuth>} />
      <Route path="/taxes" element={<RequireAuth><TaxesPage /></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
      <Route path="/help" element={<RequireAuth><HelpPage /></RequireAuth>} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MobileWarning() {
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end md:hidden overflow-y-auto">
      <div className="w-full bg-[#0f1419] border-t border-white/10 rounded-t-[2rem] p-8 shadow-2xl">
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-6" />
        <h3 className="text-white font-black text-lg tracking-tight mb-2">Desktop Recommended</h3>
        <p className="text-white/50 text-sm font-light leading-relaxed mb-6">FirstHold is designed for desktop. Some features may not display correctly on this screen size.</p>
        <button
          onClick={() => setDismissed(true)}
          className="w-full py-4 bg-white text-[#0f1419] text-xs font-black uppercase tracking-[0.2em] rounded-2xl"
        >
          Got it, continue anyway
        </button>
      </div>
    </div>
  );
}

function BottomNav() {
  const location = useLocation();
  const navItems = [
    { icon: HomeIcon, path: "/" },
    { icon: PieChartIcon, path: "/stats" },
    { icon: CalendarDaysIcon, path: "/calendar" },
    { icon: ReceiptIcon, path: "/invoice" },
    { icon: CalculatorIcon, path: "/taxes" },
    { icon: HelpCircleIcon, path: "/help" },
    { icon: SettingsIcon, path: "/settings" },
  ];
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex md:hidden items-center justify-around bg-white/80 backdrop-blur-xl border-t border-white/60 shadow-xl px-4 py-3 [@supports_not_(backdrop-filter:blur(1px))]:bg-white">
      {navItems.map((item, i) => {
        const isActive = location.pathname === item.path;
        return (
          <Link key={i} to={item.path}>
            <div className={`flex flex-col items-center justify-center w-10 h-10 rounded-xl transition-all ${isActive ? "bg-[#1A1F36] text-white" : "text-gray-400"}`}>
              <item.icon size={20} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const isPublic = PUBLIC_ROUTES.includes(location.pathname);
  const [isDark, setIsDark] = useState(false);
  const [currency, setCurrency] = useState<string>("USD");
  const [dateFormat, setDateFormatState] = useState<"auto" | "us" | "eu">("auto");
  const [holidayCalendar, setHolidayCalendarState] = useState<HolidayCalendar>("US");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const { user, loading } = useAuth();

  // Identify user in Sentry so errors are tied to an account
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email ?? undefined });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Load theme + currency from Supabase app_settings on login
  React.useEffect(() => {
    if (!user || settingsLoaded) return;
    supabase.from('app_settings').select('theme, currency, date_format_override, holiday_calendar').eq('user_id', user.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setIsDark(data.theme === 'dark');
          if (data.currency) setCurrency(data.currency);
          if (data.date_format_override === 'us' || data.date_format_override === 'eu') {
            setDateFormatState(data.date_format_override);
          }
          const cal = data.holiday_calendar as HolidayCalendar | null;
          if (cal && ['US','UK','CA','AU','DE','FR','NONE'].includes(cal)) {
            setHolidayCalendarState(cal);
          }
        }
        setSettingsLoaded(true);
      });
  }, [user, settingsLoaded]);

  const saveSettings = async (theme: string, cur: string) => {
    if (!user) return;
    const { error } = await supabase.from('app_settings')
      .upsert({ user_id: user.id, theme, currency: cur }, { onConflict: 'user_id' });
    if (error) console.error('[AppShell] saveSettings failed:', error);
  };

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    saveSettings(next ? 'dark' : 'light', currency);
  };

  const toggleCurrency = () => {
    const next = currency === "USD" ? "GBP" : currency === "GBP" ? "EUR" : currency === "EUR" ? "NONE" : "USD";
    setCurrency(next);
    saveSettings(isDark ? 'dark' : 'light', next);
  };

  const setCurrencyDirect = (v: string) => {
    setCurrency(v);
  };

  const setDateFormat = async (fmt: "auto" | "us" | "eu") => {
    setDateFormatState(fmt);
    if (!user) return;
    const { error } = await supabase.from('app_settings')
      .upsert({ user_id: user.id, date_format_override: fmt }, { onConflict: 'user_id' });
    if (error) console.error('[AppShell] setDateFormat failed:', error);
  };

  const setHolidayCalendar = async (cal: HolidayCalendar) => {
    setHolidayCalendarState(cal);
    if (!user) return;
    const { error } = await supabase.from('app_settings')
      .upsert({ user_id: user.id, holiday_calendar: cal }, { onConflict: 'user_id' });
    if (error) console.error('[AppShell] setHolidayCalendar failed:', error);
  };

  const rootBg = isDark ? "bg-[#0f1419]" : "bg-[#E3E8F1]";

  // Only render the full authenticated shell (sidebar, nav) when we have a
  // confirmed authenticated user. During loading OR on public routes OR when
  // there is no user (incognito, logged-out, BroadcastChannel cross-tab injection)
  // we fall through to the plain layout so the sidebar never flashes or renders
  // for unauthenticated visitors.
  const useAuthenticatedShell = !isPublic && !loading && !!user;

  if (!useAuthenticatedShell) {
    return (
      <ThemeContext.Provider value={{ isDark, toggleTheme, currency, toggleCurrency, setCurrencyDirect, dateFormat, setDateFormat, holidayCalendar, setHolidayCalendar }}>
        <div className={cn("min-h-screen w-full", rootBg)}>
          <ErrorBoundary>
            <Router />
          </ErrorBoundary>
        </div>
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, currency, toggleCurrency, setCurrencyDirect, dateFormat, setDateFormat, holidayCalendar, setHolidayCalendar }}>
      <div className={cn("min-h-screen w-full transition-colors duration-300 overflow-x-hidden", isDark ? "dark" : "", rootBg)}>
        <SidebarProvider defaultOpen className="bg-transparent min-h-screen">
          <div className="flex flex-col min-h-screen w-full bg-transparent">
            {/* Trial banner — full viewport width, above sidebar + content */}
            <TrialBanner
              pageContext={
                location.pathname === '/stats'   ? 'stats'   :
                location.pathname === '/taxes'   ? 'taxes'   :
                location.pathname === '/invoice' ? 'invoice' :
                'general'
              }
              onChoosePlan={() => setShowPlanModal(true)}
            />
            <div className="flex flex-1 w-full bg-transparent">
              <div className="hidden md:block"><AppSidebar /></div>
              <SidebarInset className="bg-transparent flex-1">
                <ErrorBoundary>
                  <Router />
                </ErrorBoundary>
                <MobileWarning />
                <BottomNav />
                <div className="hidden md:block text-center pb-8 pt-4">
                  <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", isDark ? "text-white/20" : "text-[#1A1F36]/20")}>
                    © 2026 FirstHold &nbsp;·&nbsp;
                    <a href="/privacy" className="hover:opacity-60 transition-opacity">Privacy Policy</a>
                    &nbsp;·&nbsp;
                    <a href="/terms" className="hover:opacity-60 transition-opacity">Terms of Service</a>
                  </p>
                </div>
              </SidebarInset>
            </div>
          </div>
        </SidebarProvider>

        {/* Subscription modal — opened from "Choose Plan" in TrialBanner (dismissible) */}
        <SubscriptionModal open={showPlanModal} onClose={() => setShowPlanModal(false)} />
      </div>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Analytics />
        <SpeedInsights />
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
